import { Permission } from '@bookora/shared';

import type { StepContext } from './types';

/**
 * Every Persian string of the guidance surfaces that is not a tour step
 * (tour spec §6.1–§6.3, §6.9), verbatim. `registry.test.ts` sweeps this file
 * with the same rules as the steps: no Latin letters, no ASCII digits, Persian
 * «ی/ک», and none of the retired vocabulary.
 */

export interface WelcomeCopyInput {
  name: string | null;
  clinicName: string | null;
  roleName: string | null;
  anotherClinic: boolean;
}

function withName(name: string | null, sentence: string): string {
  return name ? `${name}، ${sentence}` : sentence;
}

export const COPY = {
  welcome: {
    title: 'به بوکورا خوش آمدید',
    titleAnotherClinic: (clinicName: string) => `به ${clinicName} خوش آمدید`,
    roleLine: (roleName: string) => `نقش شما: ${roleName}`,
    primary: 'نمایش راهنما',
    secondary: 'بعداً',
    footnote: 'هر وقت خواستید، از «راهنما» دوباره ببینید.',
    footnoteWide: 'از دکمهٔ «راهنما» در بالای صفحه دوباره ببینید.',
    footnoteCompact: 'از منوی حساب، بخش «راهنما»، دوباره ببینید.',
    body(
      ctx: Pick<StepContext, 'persona' | 'ownerVariant' | 'can'>,
      input: WelcomeCopyInput,
    ): string {
      const { name } = input;
      if (input.anotherClinic && input.roleName) {
        return `نقش شما در این کلینیک «${input.roleName}» است و ممکن است بخش‌های دیگری ببینید. راهنمای آشنایی با پنل این کلینیک رو هم می‌بینید؟`;
      }
      switch (ctx.persona) {
        case 'owner':
          if (ctx.ownerVariant === 'new-owner') {
            return withName(
              name,
              // True through the core tour's finish (registry.ts): its last
              // button is «ادامهٔ راه‌اندازی» while setup is open.
              'حساب کلینیک ساخته شد. با بخش‌های پنل آشنا بشید. بعد از پایان راهنما، می‌تونید راه‌اندازی کلینیک رو ادامه بدید.',
            );
          }
          if (ctx.ownerVariant === 'solo') {
            return withName(
              name,
              'نوبت‌ها، پرونده بیماران، ثبت جلسه درمان و حساب‌وکتاب مطب شما همه همین‌جاست. با بخش‌های پنل آشنا بشید.',
            );
          }
          return withName(
            name,
            `پنل «${input.clinicName ?? 'کلینیک'}» آماده است. در راهنمای کوتاه نشونتون می‌دیم هر چیزی کجاست تا بعد راه‌اندازی کلینیک رو ادامه بدید.`,
          );
        case 'doctor':
          return withName(
            name,
            'نوبت‌های امروز، پرونده بیماران و ثبت جلسه درمان همه همین‌جاست. در راهنمای کوتاه نشونتون می‌دیم.',
          );
        case 'clinical':
          return withName(
            name,
            'نوبت‌های امروز، پرونده بیماران و ثبت جلسه درمان همه همین‌جاست. در راهنمای کوتاه نشونتون می‌دیم.',
          );
        case 'front-desk':
          return ctx.can(Permission.APPOINTMENT_CONFIRM_REQUESTS)
            ? withName(
                name,
                'نوبت‌دهی، پیدا کردن پرونده بیمار و رسیدگی به درخواست‌های آنلاین همه همین‌جاست. در راهنمای کوتاه نشونتون می‌دیم.',
              )
            : withName(
                name,
                'نوبت‌دهی و پیدا کردن پرونده بیمار همه همین‌جاست. در راهنمای کوتاه نشونتون می‌دیم.',
              );
        case 'finance':
          return withName(
            name,
            'دریافت‌ها، هزینه‌ها و گزارش سود و زیان کلینیک همه همین‌جاست. با بخش‌های پنل آشنا بشید.',
          );
        default:
          return withName(name, 'در راهنمای کوتاه نشونتون می‌دیم هر چیزی در پنل کجاست.');
      }
    },
  },

  announcement: {
    title: 'راهنمای بوکورا',
    bodyWide: 'راهنمای کوتاه پنل و بخش‌هاش رو هر وقت خواستید، از همین دکمه ببینید.',
    bodyCompact: 'راهنمای کوتاه پنل و بخش‌هاش رو از منوی حساب، بخش «راهنما»، ببینید.',
    primary: 'نمایش راهنما',
    secondary: 'متوجه شدم',
  },

  offer: {
    title: (tourTitle: string) => `راهنمای ${tourTitle}`,
    footnote: 'بعداً هم از «راهنما» در دسترس است.',
    primary: 'نمایش راهنما',
    secondary: 'نه، ممنون',
    live: 'راهنمای این بخش آماده است',
  },

  help: {
    trigger: 'راهنما',
    triggerWithBadge: (n: string) => `راهنما، ${n} پیشنهاد جدید`,
    page: 'راهنمای این صفحه',
    pageContinue: 'ادامهٔ راهنمای این صفحه',
    core: 'آشنایی با پنل',
    hub: 'راهنما و پیشنهادها',
    support: 'تماس با پشتیبانی',
    navRow: 'راهنما و پیشنهادها',
  },

  hub: {
    title: 'راهنما و پیشنهادها',
    intro: 'راهنماها کوتاه‌اند. هر وقت نیاز داشتید، می‌تونید دوباره ببینیدشون.',
    /** On a touch screen only: the kit's long-press shows a control's label. */
    touchHint: 'برای دیدن نام هر دکمه، انگشتتون رو کمی روی اون نگه دارید.',
    gettingStarted: 'شروع کار',
    sections: 'راهنمای بخش‌ها',
    setupTitle: 'راه‌اندازی کلینیک',
    /** Rendered with `<Num>` around each number; this is the sentence around them. */
    setupProgressJoin: ' از ',
    setupProgressTail: ' مرحلهٔ ضروری انجام شده',
    setupCta: 'ادامهٔ راه‌اندازی',
    tags: { new: 'جدید', inProgress: 'نیمه‌کاره', done: 'دیدید', updated: 'به‌روز شده' },
    buttons: { show: 'نمایش', resume: 'ادامه', again: 'دوباره' },
    context: {
      patient: { helper: 'از پروندهٔ یک بیمار شروع می‌شه.', button: 'رفتن به بیماران' },
      // An armed context tour waits a working day in this tab
      // (`CONTEXT_ARMED_TTL_MS`, with «لغو» on the armed notice), so «دفعهٔ بعد» holds.
      'session-form': {
        helper: 'دفعهٔ بعد که فرم «ثبت جلسه درمان» رو باز کنید، شروع می‌شه.',
        button: 'آماده کردن راهنما',
      },
      'appointment-form': {
        helper: 'دفعهٔ بعد که فرم «ثبت نوبت» رو باز کنید، شروع می‌شه.',
        button: 'آماده کردن راهنما',
      },
      'import-job': {
        helper: 'از صفحهٔ بازبینی اطلاعات خوانده‌شده از روی عکس شروع می‌شه.',
        button: 'رفتن به ثبت اطلاعات از روی عکس',
      },
    },
    reset: 'نمایش دوبارهٔ همهٔ راهنماها',
    resetConfirmTitle: 'همهٔ راهنماها از نو نمایش داده بشن؟',
    resetConfirmContent:
      'خوش‌آمدگویی و راهنمای بخش‌ها دوباره مثل روز اول پیشنهاد می‌شن. هیچ اطلاعاتی از کلینیک تغییر نمی‌کنه.',
    resetOk: 'بله، از نو',
    resetCancel: 'انصراف',
    resetDone: 'راهنماها از نو فعال شدند.',
    impersonating: 'در حالت ورود به‌جای کاربر، تنظیمات راهنما تغییر نمی‌کنه.',
    loadError: 'وضعیت راهنماها بارگذاری نشد. خود راهنماها همچنان در دسترس‌اند.',
    retry: 'تلاش دوباره',
    support: 'تماس با پشتیبانی',
  },

  toast: {
    unavailable: 'این راهنما الان نمایش داده نمی‌شه. وقتی صفحه کامل باز شد دوباره امتحان کنید.',
    armed: {
      patient: 'پروندهٔ یک بیمار رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
      'session-form': 'فرم «ثبت جلسه درمان» رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
      'appointment-form': 'فرم «ثبت نوبت» رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
      'import-job':
        'برای بازبینی، یکی از موارد «ثبت اطلاعات از روی عکس» رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
    },
    declinesPaused:
      'تا یک هفته راهنما به شما پیشنهاد نمی‌شه. هر زمان بخواید، می‌تونید از بخش «راهنما» اون رو باز کنید.',
  },

  chip: {
    label: 'راهنمای این بخش',
    resume: 'ادامهٔ راهنما',
    updated: 'به‌روز شده',
    inlineRow: 'راهنمای این فرم',
    inlineRegion: 'راهنمای این فرم',
  },
} as const;
